import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Progress } from "../components/ui/progress";
import {
  UserCog,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Search,
  Shield,
  Mail,
  Calendar,
  Check,
  X,
  UserCheck,
  UserX,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Filter,
  Home,
  Merge,
  RotateCw
} from "lucide-react";

export default function UsuariosPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [supervisorSearch, setSupervisorSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, inactive
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [departamentos, setDepartamentos] = useState([]);
  const [dynamicRoles, setDynamicRoles] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "analyst",
    is_active: true,
    phone: "",
    departamento_id: "",
    position: "",
    supervisor_id: "",
    home_page: "/dashboard/mi-dashboard",  // Por defecto: Mi Dashboard
    home_page_custom: "",  // URL personalizada para ventana de inicio
    force_password_change: true,  // Por defecto, nuevos usuarios deben cambiar contraseña
    can_delete_requisiciones: false,  // Permiso para eliminar requisiciones
    can_delete_transacciones_financieras: false,  // Permiso para eliminar transacciones de caja chica y cuentas por pagar
    can_authorize_financial_deletions: false,  // Permiso para autorizar eliminación de transacciones financieras
    es_aprobador_requisiciones: false,  // Puede ver y aprobar todas las requisiciones
    puede_editar_caja_chica: false,  // Puede editar transacciones de caja chica
    can_delete_movimientos_gh: false,  // Puede eliminar movimientos de Gestión Humana
    can_view_all_mensajeria: false,  // Puede ver toda la mensajería
    puede_publicar: false,  // Puede publicar en Informaciones Generales
    cotizaciones_departamentos: [],  // Departamentos de cotizaciones permitidos
    fecha_aniversario: "",  // Fecha de ingreso a la empresa (base para vacaciones automáticas)
    mantener_visible_inactivo: false,  // Mantener info visible para supervisores aunque esté inactivo
    puede_cambiar_vista: false,  // Puede usar el selector "Ver como" (vista por rol)
    marketing_solo_estadisticas: false,  // Solo ver estadísticas en Artículos de Marketing
    lionbridge_ver_todo: false,  // Proyecto LionBridge: ver todo, solo lectura
    solo_lectura_encuestas: false,  // Encuestas: solo lectura (solo puede copiar links)
    solo_resultados_propios: false,  // Encuestas: solo ve resultados de sus propias encuestas
    delegaciones_visibilidad: []  // Cobertura de Supervisor: [{supervisor_id, modulos, activo}]
  });
  const { getAuthHeaders, user: currentUser } = useAuth();
  const [delegacionFiltro, setDelegacionFiltro] = useState({});  // filtro de búsqueda por delegación (idx → texto)
  const [showHistorialCoberturas, setShowHistorialCoberturas] = useState(false);
  const [historialCoberturas, setHistorialCoberturas] = useState([]);
  const [loadingHistCoberturas, setLoadingHistCoberturas] = useState(false);

  const abrirHistorialCoberturas = async () => {
    setShowHistorialCoberturas(true);
    setLoadingHistCoberturas(true);
    try {
      const res = await axios.get(`${API}/delegaciones/historial`, getAuthHeaders());
      setHistorialCoberturas(res.data?.historial || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar el historial de coberturas");
    } finally {
      setLoadingHistCoberturas(false);
    }
  };

  // Bulk import states
  const [showBulkDialog, setShowBulkDialog] = useState(false);

  // Consolidate duplicates state
  const [showConsolidateDialog, setShowConsolidateDialog] = useState(false);
  const [consolidating, setConsolidating] = useState(false);
  const [canonicalName, setCanonicalName] = useState("");
  const [aliasesInput, setAliasesInput] = useState("");
  const [consolidateResult, setConsolidateResult] = useState(null);
  const [autoSuggestions, setAutoSuggestions] = useState(null);
  const [scanningDuplicates, setScanningDuplicates] = useState(false);

  const fetchDuplicateSuggestions = async () => {
    setScanningDuplicates(true);
    setAutoSuggestions(null);
    try {
      const res = await axios.get(`${API}/admin/detect-duplicate-users?threshold=0.85`, getAuthHeaders());
      setAutoSuggestions(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al detectar duplicados");
    } finally {
      setScanningDuplicates(false);
    }
  };

  const applySuggestion = (sug) => {
    setCanonicalName(sug.canonical || "");
    setAliasesInput((sug.aliases || []).map((a) => a.name).join("\n"));
    setConsolidateResult(null);
    toast.info(`Sugerencia cargada: ${sug.canonical} con ${sug.aliases?.length || 0} alias`);
  };

  const handleConsolidate = async (dryRun) => {
    const aliases = aliasesInput
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((s) => s !== canonicalName.trim());
    if (!canonicalName.trim() || aliases.length === 0) {
      toast.error("Ingresa el nombre canónico y al menos un alias distinto");
      return;
    }
    setConsolidating(true);
    try {
      // Look up canonical user id by name (best-effort)
      const canonical = users.find((u) => u.name?.trim() === canonicalName.trim());
      const res = await axios.post(
        `${API}/admin/consolidate-user`,
        {
          canonical_name: canonicalName.trim(),
          canonical_user_id: canonical?.id,
          aliases,
          delete_alias_users: true,
          dry_run: !!dryRun,
        },
        getAuthHeaders()
      );
      setConsolidateResult(res.data);
      toast.success(
        dryRun
          ? "Vista previa generada — revisa los conteos"
          : `Consolidación aplicada · ${res.data.users_deleted?.length || 0} usuarios eliminados`
      );
      if (!dryRun) {
        await fetchUsers();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al consolidar");
    } finally {
      setConsolidating(false);
    }
  };
  const [bulkStep, setBulkStep] = useState(1); // 1: upload, 2: mapping, 3: result
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkMapping, setBulkMapping] = useState({});
  const [bulkResult, setBulkResult] = useState(null);
  const fileInputRef = useRef(null);

  // Default role colors for display
  const roleColors = {
    admin: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    administrador: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    analyst: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    analista: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    viewer: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
    visualizador: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
    gestion_humana: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    recepcion: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    mensajero: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
    supervisor: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
    ejecutivos_rrhh: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300"
  };

  // Campos disponibles para mapeo en importación Excel (incluye supervisor y departamento)
  const userFields = [
    { value: "name", label: "Nombre" },
    { value: "email", label: "Email" },
    { value: "password", label: "Contraseña" },
    { value: "role", label: "Rol" },
    { value: "phone", label: "Teléfono" },
    { value: "department", label: "Departamento" },
    { value: "position", label: "Cargo" },
    { value: "supervisor", label: "Supervisor" },
    { value: "is_active", label: "Estado Activo" }
  ];

  useEffect(() => {
    fetchUsers();
    fetchDepartamentos();
    fetchRoles();
    fetchMenus();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, getAuthHeaders());
      setUsers(res.data);
    } catch (err) {
      toast.error("Error al cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartamentos = async () => {
    try {
      const res = await axios.get(`${API}/departamentos`, getAuthHeaders());
      setDepartamentos(res.data);
    } catch (err) {
      console.error("Error al cargar departamentos:", err);
    }
  };

  const [menus, setMenus] = useState([]);
  
  const fetchMenus = async () => {
    try {
      const res = await axios.get(`${API}/system/menus`, getAuthHeaders());
      // Flatten menus to include submenus with their paths
      const flatMenus = [];
      res.data.forEach(menu => {
        if (menu.path) {
          flatMenus.push({ id: menu.path, label: menu.label });
        }
        if (menu.submenus) {
          menu.submenus.forEach(sub => {
            if (sub.path) {
              flatMenus.push({ id: sub.path, label: `${menu.label} > ${sub.label}` });
            }
          });
        }
      });
      setMenus(flatMenus);
    } catch (err) {
      console.error("Error al cargar menús:", err);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API}/roles`, getAuthHeaders());
      // Transform roles to have value, label, and color
      const rolesData = res.data.map(role => ({
        value: role.id,
        label: role.name,
        color: roleColors[role.id] || "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
      }));
      setDynamicRoles(rolesData);
    } catch (err) {
      console.error("Error al cargar roles:", err);
      // Fallback to default roles if API fails
      setDynamicRoles([
        { value: "admin", label: "Administrador", color: roleColors.admin },
        { value: "analyst", label: "Analista", color: roleColors.analyst },
        { value: "viewer", label: "Visualizador", color: roleColors.viewer }
      ]);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "analyst",
      is_active: true,
      phone: "",
      departamento_id: "",
      position: "",
      supervisor_id: "",
      home_page: "/dashboard/mi-dashboard",  // Por defecto: Mi Dashboard
      home_page_custom: "",
      force_password_change: true,  // Por defecto, nuevos usuarios deben cambiar contraseña
      can_delete_requisiciones: false,
      can_delete_transacciones_financieras: false,
      can_authorize_financial_deletions: false,
      es_aprobador_requisiciones: false,
      puede_editar_caja_chica: false,
      can_delete_movimientos_gh: false,
      can_view_all_mensajeria: false,
      cotizaciones_departamentos: [],
      fecha_aniversario: "",
      mantener_visible_inactivo: false,
      puede_cambiar_vista: false,
      marketing_solo_estadisticas: false,
      lionbridge_ver_todo: false,
      solo_lectura_encuestas: false,
      solo_resultados_propios: false,
      delegaciones_visibilidad: []
    });
    setEditingUser(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    // Find the role ID if user.role is a name instead of ID
    let roleValue = user.role || "analyst";
    const roleByName = dynamicRoles.find(r => r.label === user.role);
    if (roleByName) {
      roleValue = roleByName.value;
    }
    
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: roleValue,
      is_active: user.is_active !== false,
      phone: user.phone || "",
      departamento_id: user.departamento_id || "",
      position: user.position || "",
      supervisor_id: user.supervisor_id || "",
      home_page: user.home_page || "",
      force_password_change: user.force_password_change || false,
      can_delete_requisiciones: user.can_delete_requisiciones || false,
      can_delete_transacciones_financieras: user.can_delete_transacciones_financieras || false,
      can_authorize_financial_deletions: user.can_authorize_financial_deletions || false,
      es_aprobador_requisiciones: user.es_aprobador_requisiciones || false,
      puede_editar_caja_chica: user.puede_editar_caja_chica || false,
      can_delete_movimientos_gh: user.can_delete_movimientos_gh || false,
      can_view_all_mensajeria: user.can_view_all_mensajeria || false,
      cotizaciones_departamentos: user.cotizaciones_departamentos || [],
      fecha_aniversario: user.fecha_aniversario ? user.fecha_aniversario.substring(0, 10) : "",
      mantener_visible_inactivo: user.mantener_visible_inactivo || false,
      puede_cambiar_vista: user.puede_cambiar_vista || false,
      marketing_solo_estadisticas: user.marketing_solo_estadisticas || false,
      lionbridge_ver_todo: user.lionbridge_ver_todo || false,
      solo_lectura_encuestas: user.solo_lectura_encuestas || false,
      solo_resultados_propios: user.solo_resultados_propios || false,
      delegaciones_visibilidad: user.delegaciones_visibilidad || []
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    // Validación de campos requeridos
    if (!formData.name?.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    
    if (!formData.email?.trim()) {
      toast.error("El email es requerido");
      return;
    }
    
    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("El email no tiene un formato válido. Debe ser como: usuario@dominio.com");
      return;
    }
    
    if (!editingUser && !formData.password?.trim()) {
      toast.error("La contraseña es requerida para nuevos usuarios");
      return;
    }
    
    setSaving(true);
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          is_active: formData.is_active,
          phone: formData.phone,
          departamento_id: formData.departamento_id || null,
          position: formData.position,
          supervisor_id: formData.supervisor_id || null,
          home_page: formData.home_page === "custom" ? formData.home_page_custom : (formData.home_page || null),
          force_password_change: formData.force_password_change || false,
          can_delete_requisiciones: formData.can_delete_requisiciones || false,
          can_delete_transacciones_financieras: formData.can_delete_transacciones_financieras || false,
          can_authorize_financial_deletions: formData.can_authorize_financial_deletions || false,
          es_aprobador_requisiciones: formData.es_aprobador_requisiciones || false,
          puede_editar_caja_chica: formData.puede_editar_caja_chica || false,
          can_delete_movimientos_gh: formData.can_delete_movimientos_gh || false,
          can_view_all_mensajeria: formData.can_view_all_mensajeria || false,
          cotizaciones_departamentos: formData.cotizaciones_departamentos || [],
          fecha_aniversario: formData.fecha_aniversario || null,
          mantener_visible_inactivo: formData.mantener_visible_inactivo || false,
          marketing_solo_estadisticas: formData.marketing_solo_estadisticas || false,
          lionbridge_ver_todo: formData.lionbridge_ver_todo || false,
          solo_lectura_encuestas: formData.solo_lectura_encuestas || false,
          solo_resultados_propios: formData.solo_resultados_propios || false,
          puede_cambiar_vista: formData.puede_cambiar_vista || false,
          delegaciones_visibilidad: formData.delegaciones_visibilidad || [],
          ...(formData.password && { password: formData.password })
        }, getAuthHeaders());
        toast.success("Usuario actualizado");
      } else {
        await axios.post(`${API}/users`, {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          is_active: formData.is_active,
          phone: formData.phone,
          departamento_id: formData.departamento_id || null,
          position: formData.position,
          supervisor_id: formData.supervisor_id || null,
          home_page: formData.home_page === "custom" ? formData.home_page_custom : (formData.home_page || null),
          force_password_change: formData.force_password_change !== false,
          can_delete_requisiciones: formData.can_delete_requisiciones || false,
          can_delete_transacciones_financieras: formData.can_delete_transacciones_financieras || false,
          can_authorize_financial_deletions: formData.can_authorize_financial_deletions || false,
          es_aprobador_requisiciones: formData.es_aprobador_requisiciones || false,
          puede_editar_caja_chica: formData.puede_editar_caja_chica || false,
          can_delete_movimientos_gh: formData.can_delete_movimientos_gh || false,
          can_view_all_mensajeria: formData.can_view_all_mensajeria || false,
          cotizaciones_departamentos: formData.cotizaciones_departamentos || [],
          fecha_aniversario: formData.fecha_aniversario || null,
          mantener_visible_inactivo: formData.mantener_visible_inactivo || false,
          marketing_solo_estadisticas: formData.marketing_solo_estadisticas || false,
          lionbridge_ver_todo: formData.lionbridge_ver_todo || false,
          solo_lectura_encuestas: formData.solo_lectura_encuestas || false,
          solo_resultados_propios: formData.solo_resultados_propios || false,
          puede_cambiar_vista: formData.puede_cambiar_vista || false
        }, getAuthHeaders());
        toast.success("Usuario creado");
      }
      setShowDialog(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      console.error("Error saving user:", err.response?.data);
      // Parse error message for better user feedback
      const errorDetail = err.response?.data?.detail;
      let errorMessage = "Error al guardar el usuario";
      
      if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        const fieldErrors = errorDetail.map(e => {
          const field = e.loc?.[1] || e.loc?.[0] || 'campo';
          const fieldNames = {
            'email': 'Email',
            'name': 'Nombre',
            'password': 'Contraseña',
            'role': 'Rol',
            'phone': 'Teléfono'
          };
          const fieldName = fieldNames[field] || field;
          
          if (e.msg?.includes('email address')) {
            return `${fieldName}: Debe ser un correo electrónico válido (ejemplo: usuario@dominio.com)`;
          }
          return `${fieldName}: ${e.msg}`;
        });
        errorMessage = fieldErrors.join('\n');
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      // Find the role ID if user.role is a name instead of ID
      let roleValue = user.role || "analyst";
      const roleByName = dynamicRoles.find(r => r.label === user.role);
      if (roleByName) {
        roleValue = roleByName.value;
      }
      
      await axios.put(`${API}/users/${user.id}`, {
        name: user.name,
        email: user.email,
        role: roleValue,
        is_active: !user.is_active,
        phone: user.phone || "",
        departamento_id: user.departamento_id || null,
        position: user.position || "",
        supervisor_id: user.supervisor_id || null,
        home_page: user.home_page || null
      }, getAuthHeaders());
      toast.success(user.is_active ? "Usuario desactivado" : "Usuario activado");
      fetchUsers();
    } catch (err) {
      console.error("Error toggling user status:", err.response?.data);
      const errorDetail = err.response?.data?.detail;
      
      if (Array.isArray(errorDetail)) {
        const fieldError = errorDetail.find(e => e.loc?.[1] === 'email');
        if (fieldError) {
          toast.error(`No se puede cambiar el estado: El usuario tiene un email inválido (${user.email}). Edita el usuario primero para corregir el email.`);
          return;
        }
      }
      toast.error("Error al cambiar estado del usuario");
    }
  };

  const deleteUser = async (id) => {
    if (id === currentUser?.id) {
      toast.error("No puedes eliminar tu propia cuenta");
      return;
    }
    if (!window.confirm("¿Estás seguro de eliminar este usuario?")) return;
    try {
      await axios.delete(`${API}/users/${id}`, getAuthHeaders());
      toast.success("Usuario eliminado");
      setUsers(users.filter(u => u.id !== id));
    } catch (err) {
      toast.error("Error al eliminar el usuario");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getRoleBadge = (role) => {
    const roleInfo = dynamicRoles.find(r => r.value === role);
    const color = roleInfo?.color || roleColors[role] || "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300";
    const label = roleInfo?.label || role;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {label}
      </span>
    );
  };

  // Bulk Import Functions
  const openBulkDialog = () => {
    setBulkStep(1);
    setBulkPreview(null);
    setBulkMapping({});
    setBulkResult(null);
    setShowBulkDialog(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBulkLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/users/bulk-import/preview`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setBulkPreview(res.data);
      setBulkMapping(res.data.column_mapping || {});
      setBulkStep(2);
      toast.success(`${res.data.total_rows} filas encontradas. La IA ha mapeado los campos automáticamente.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar el archivo");
    } finally {
      setBulkLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const updateMapping = (excelCol, userField) => {
    setBulkMapping(prev => {
      const newMapping = { ...prev };
      if (userField) {
        newMapping[excelCol] = userField;
      } else {
        delete newMapping[excelCol];
      }
      return newMapping;
    });
  };

  const executeBulkImport = async () => {
    if (!bulkPreview) return;

    // Validate email mapping exists
    const hasEmailMapping = Object.values(bulkMapping).includes('email');
    if (!hasEmailMapping) {
      toast.error("Debe mapear una columna al campo 'Email'");
      return;
    }

    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/users/bulk-import/execute`, {
        column_mapping: bulkMapping,
        default_password: bulkPreview.default_password,
        default_role: bulkPreview.default_role,
        rows: bulkPreview.all_data
      }, getAuthHeaders());

      setBulkResult(res.data);
      setBulkStep(3);
      
      if (res.data.created > 0) {
        toast.success(`${res.data.created} usuarios creados exitosamente`);
        fetchUsers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar usuarios");
    } finally {
      setBulkLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    const matchesStatus = statusFilter === "all" || 
                         (statusFilter === "active" && user.is_active !== false) ||
                         (statusFilter === "inactive" && user.is_active === false);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Stats
  const activeCount = users.filter(u => u.is_active !== false).length;
  const inactiveCount = users.filter(u => u.is_active === false).length;

  const isAdmin = currentUser?.role?.toLowerCase() === 'administrador' || currentUser?.role?.toLowerCase() === 'admin';

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="usuarios-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Gestión de Usuarios
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Administra los usuarios y sus permisos
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            {isAdmin && (
              <Button 
                onClick={() => { setConsolidateResult(null); setShowConsolidateDialog(true); fetchDuplicateSuggestions(); }}
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-950/30"
                data-testid="consolidate-users-button"
                title="Detecta y unifica duplicados con typos en un solo usuario canónico"
              >
                <Merge className="w-4 h-4 mr-2" />
                Consolidar duplicados
              </Button>
            )}
            {isAdmin && (
              <Button 
                onClick={openBulkDialog}
                variant="outline"
                className="border-[#0C314C] text-[#0C314C] hover:bg-[#0C314C]/10"
                data-testid="bulk-import-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importar Excel
              </Button>
            )}
            <Button 
              onClick={openNewDialog}
              className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              data-testid="new-user-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Usuario
            </Button>
          </div>
        </div>

        {/* Stats Cards - Clickable filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "all" && !searchQuery ? "ring-2 ring-slate-500" : ""}`}
            onClick={() => { setStatusFilter("all"); setSearchQuery(""); }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Usuarios</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{users.length}</p>
                </div>
                <UserCog className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "active" ? "ring-2 ring-green-500" : ""}`}
            onClick={() => { setStatusFilter("active"); setSearchQuery(""); }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Activos</p>
                  <p className="text-2xl font-bold text-green-600">{activeCount}</p>
                </div>
                <UserCheck className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "inactive" ? "ring-2 ring-red-500" : ""}`}
            onClick={() => { setStatusFilter("inactive"); setSearchQuery(""); }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Inactivos</p>
                  <p className="text-2xl font-bold text-red-600">{inactiveCount}</p>
                </div>
                <UserX className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 shadow-sm mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Buscar por nombre o email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-slate-200 dark:border-slate-600"
                  data-testid="user-search-input"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full md:w-[180px] border-slate-200 dark:border-slate-600">
                  <Shield className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los roles</SelectItem>
                  {dynamicRoles.map(role => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-[180px] border-slate-200 dark:border-slate-600" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="active">Solo Activos</SelectItem>
                  <SelectItem value="inactive">Solo Inactivos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-100 dark:border-slate-700">
            <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white font-['Manrope']">
              Usuarios Registrados ({filteredUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <UserCog className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400">No se encontraron usuarios</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Usuario</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Email</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Rol</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Estado</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Registro</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className={`hover:bg-slate-50 dark:hover:bg-slate-700/30 ${user.is_active === false ? 'opacity-60' : ''}`} data-testid={`user-row-${user.id}`}>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.is_active === false ? 'bg-slate-400' : 'bg-[#0C314C]'}`}>
                              <span className="text-white text-sm font-medium">
                                {user.name?.charAt(0)?.toUpperCase() || "U"}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium text-slate-900 dark:text-white">{user.name}</span>
                              {user.position && (
                                <p className="text-xs text-slate-500 dark:text-slate-400">{user.position}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                            <Mail className="w-4 h-4 text-slate-400" />
                            {user.email}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="py-4 px-4">
                          <button
                            onClick={() => toggleUserStatus(user)}
                            className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                              user.is_active !== false
                                ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300'
                            }`}
                            data-testid={`toggle-status-${user.id}`}
                          >
                            {user.is_active !== false ? (
                              <>
                                <UserCheck className="w-3 h-3" />
                                Activo
                              </>
                            ) : (
                              <>
                                <UserX className="w-3 h-3" />
                                Inactivo
                              </>
                            )}
                          </button>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm">
                            <Calendar className="w-4 h-4" />
                            {formatDate(user.created_at)}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(user)}
                              className="text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {user.id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteUser(user.id)}
                                className="text-slate-400 hover:text-red-600"
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="text-xl font-bold font-['Manrope']">
                {editingUser ? "Editar Usuario" : "Nuevo Usuario"}
              </DialogTitle>
              <DialogDescription>
                {editingUser ? "Modifica la información del usuario" : "Completa los datos para crear un nuevo usuario"}
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto pr-2" style={{maxHeight: "65vh"}}>
            <div className="space-y-4 py-4 pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre completo *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Juan Pérez"
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="usuario@empresa.com"
                    data-testid="user-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">
                  Contraseña {editingUser ? "(dejar vacío para no cambiar)" : "*"}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                  data-testid="user-password-input"
                />
              </div>

              {/* Forzar cambio de contraseña */}
              <div className="flex items-center space-x-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <input
                  type="checkbox"
                  id="force_password_change"
                  checked={formData.force_password_change}
                  onChange={(e) => setFormData({...formData, force_password_change: e.target.checked})}
                  className="w-4 h-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
                  data-testid="force-password-checkbox"
                />
                <div>
                  <Label htmlFor="force_password_change" className="text-sm font-medium text-amber-800 dark:text-amber-300 cursor-pointer">
                    Forzar cambio de contraseña en próximo inicio
                  </Label>
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    {editingUser 
                      ? "El usuario deberá cambiar su contraseña la próxima vez que inicie sesión"
                      : "Por defecto, los nuevos usuarios deben cambiar su contraseña al iniciar sesión"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="809-555-1234"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Rol</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                    <SelectTrigger className="border-slate-200" data-testid="user-role-select">
                      <SelectValue placeholder="Seleccionar rol" />
                    </SelectTrigger>
                    <SelectContent>
                      {dynamicRoles.map(role => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="departamento">Departamento</Label>
                  <Select 
                    value={formData.departamento_id || "none"} 
                    onValueChange={(value) => setFormData({...formData, departamento_id: value === "none" ? "" : value})}
                  >
                    <SelectTrigger className="border-slate-200">
                      <SelectValue placeholder="Seleccionar departamento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin departamento</SelectItem>
                      {departamentos.map(d => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position">Cargo</Label>
                  <Input
                    id="position"
                    value={formData.position}
                    onChange={(e) => setFormData({...formData, position: e.target.value})}
                    placeholder="Ejecutivo de Ventas"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="supervisor">Supervisor</Label>
                <Select 
                  value={formData.supervisor_id || "none"} 
                  onValueChange={(value) => setFormData({...formData, supervisor_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger className="border-slate-200" data-testid="user-supervisor-select">
                    <SelectValue placeholder="Seleccionar supervisor" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[60vh]">
                    <div className="p-2 sticky top-0 bg-white z-10 border-b">
                      <Input
                        placeholder="Buscar usuario..."
                        value={supervisorSearch}
                        onChange={(e) => setSupervisorSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="h-8 text-xs"
                        data-testid="supervisor-search-input"
                      />
                    </div>
                    <SelectItem value="none">Sin supervisor</SelectItem>
                    {(() => {
                      // Obtener nombres/IDs de roles "supervisor"
                      const supervisorRoleNames = dynamicRoles
                        .filter(r => r.label?.toLowerCase().includes('supervisor'))
                        .map(r => (r.label || "").toLowerCase());
                      const supervisorRoleIds = dynamicRoles
                        .filter(r => r.label?.toLowerCase().includes('supervisor'))
                        .map(r => r.value);

                      // Excepción explícita: Aravelis Mercado (Gerente Gestión Humana)
                      // debe aparecer aunque su rol no sea "supervisor"
                      const ARAVELIS_EMAIL = "amercado@qasesores.com.do";

                      const esSupervisor = (u) => {
                        const ur = (u.role || "").toLowerCase();
                        return (
                          supervisorRoleNames.includes(ur) ||
                          supervisorRoleIds.includes(u.role) ||
                          (u.email || "").toLowerCase() === ARAVELIS_EMAIL
                        );
                      };

                      return users
                        .filter(u =>
                          u.is_active !== false &&
                          u.id !== editingUser?.id &&
                          esSupervisor(u) &&
                          (!supervisorSearch ||
                            (u.name || "").toLowerCase().includes(supervisorSearch.toLowerCase()) ||
                            (u.email || "").toLowerCase().includes(supervisorSearch.toLowerCase()) ||
                            (u.position || "").toLowerCase().includes(supervisorSearch.toLowerCase()))
                        )
                        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                        .map(u => {
                          const roleName = dynamicRoles.find(r => r.value === u.role || r.label === u.role)?.label || u.role;
                          return (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} ({u.position || roleName})
                            </SelectItem>
                          );
                        });
                    })()}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Solo usuarios con rol Supervisor (más Aravelis Mercado como excepción).</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fecha_aniversario" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Fecha Aniversario (Ingreso a la empresa)
                </Label>
                <Input
                  id="fecha_aniversario"
                  type="date"
                  value={formData.fecha_aniversario || ""}
                  onChange={(e) => setFormData({...formData, fecha_aniversario: e.target.value})}
                  data-testid="user-fecha-aniversario"
                />
                <p className="text-xs text-slate-500">Cada año, en esta fecha, el sistema acreditará automáticamente 14 días de vacaciones.</p>
              </div>

              {/* Permiso: Ver como (vista por rol) */}
              <div className="flex items-center justify-between p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <div className="pr-3">
                  <Label htmlFor="puede_cambiar_vista" className="cursor-pointer font-medium">Puede cambiar de vista (Ver como)</Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400">El usuario podrá ver la plataforma con cualquier rol (supervisor, ejecutivo, etc.) desde el selector del menú lateral, para hacer pruebas. Los administradores siempre lo tienen.</p>
                </div>
                <Switch
                  id="puede_cambiar_vista"
                  checked={formData.puede_cambiar_vista || false}
                  onCheckedChange={(checked) => setFormData({...formData, puede_cambiar_vista: checked})}
                  data-testid="user-puede-cambiar-vista-toggle"
                />
              </div>

              {/* Permiso: Solo estadísticas en Artículos de Marketing */}
              <div className="flex items-center justify-between p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                <div className="pr-3">
                  <Label htmlFor="marketing_solo_estadisticas" className="cursor-pointer font-medium">Artículos de Marketing: solo estadísticas</Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400">El usuario solo podrá ver las estadísticas de los artículos. No podrá editar, borrar, clonar ni agregar nada en ninguna pestaña (acceso de solo lectura).</p>
                </div>
                <Switch
                  id="marketing_solo_estadisticas"
                  checked={formData.marketing_solo_estadisticas || false}
                  onCheckedChange={(checked) => setFormData({...formData, marketing_solo_estadisticas: checked})}
                  data-testid="user-marketing-solo-estadisticas-toggle"
                />
              </div>

              {/* Permiso: Proyecto LionBridge (ver todo, solo lectura) */}
              <div className="flex items-center justify-between p-3 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-200 dark:border-sky-800">
                <div className="pr-3">
                  <Label htmlFor="lionbridge_ver_todo" className="cursor-pointer font-medium">Proyecto LionBridge</Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400">El usuario podrá ver todas las transacciones del Cuadre de Caja (de todos los usuarios) y exportar los reportes, sin opción de crear, editar ni borrar (solo lectura).</p>
                </div>
                <Switch
                  id="lionbridge_ver_todo"
                  checked={formData.lionbridge_ver_todo || false}
                  onCheckedChange={(checked) => setFormData({...formData, lionbridge_ver_todo: checked})}
                  data-testid="user-lionbridge-ver-todo-toggle"
                />
              </div>

              {/* Cobertura de Supervisor (delegación de visibilidad) */}
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 space-y-3" data-testid="delegaciones-section">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <Label className="font-medium">Cobertura de Supervisor (delegación)</Label>
                    <p className="text-xs text-slate-500 dark:text-slate-400">El usuario verá las transacciones del supervisor elegido y de sus supervisados en los submenús seleccionados (ej. mientras el supervisor está de vacaciones). Todo cambio que haga sobre registros del supervisor cubierto exigirá una nota de soporte.</p>
                  </div>
                  <Button type="button" size="sm" variant="outline" className="shrink-0 text-amber-700 border-amber-300 hover:bg-amber-100" onClick={abrirHistorialCoberturas} data-testid="historial-coberturas-btn">
                    Historial de Coberturas
                  </Button>
                </div>
                {(formData.delegaciones_visibilidad || []).map((d, idx) => {
                  // Solo usuarios con rol supervisor o que supervisan a alguien
                  const supervisoresBase = users.filter(u =>
                    u.id !== editingUser?.id && u.is_active !== false &&
                    ((u.role || "").toLowerCase() === "supervisor" || users.some(x => x.supervisor_id === u.id))
                  );
                  const filtro = (delegacionFiltro[idx] || "").trim().toLowerCase();
                  const opciones = supervisoresBase.filter(u =>
                    !filtro || (u.name || "").toLowerCase().includes(filtro) || u.id === d.supervisor_id
                  );
                  // Mantener visible el supervisor ya seleccionado aunque no cumpla el filtro
                  const seleccionado = users.find(u => u.id === d.supervisor_id);
                  if (seleccionado && !opciones.some(u => u.id === seleccionado.id)) opciones.unshift(seleccionado);
                  return (
                  <div key={idx} className="border border-amber-300 dark:border-amber-700 rounded-md p-2 bg-white dark:bg-slate-900 space-y-2" data-testid={`delegacion-item-${idx}`}>
                    <Input
                      placeholder="Buscar supervisor..."
                      className="h-8 text-sm"
                      value={delegacionFiltro[idx] || ""}
                      onChange={(e) => setDelegacionFiltro({ ...delegacionFiltro, [idx]: e.target.value })}
                      data-testid={`delegacion-buscar-${idx}`}
                    />
                    <div className="flex items-center gap-2">
                      <select
                        className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={d.supervisor_id || ""}
                        onChange={(e) => {
                          const arr = [...formData.delegaciones_visibilidad];
                          arr[idx] = { ...arr[idx], supervisor_id: e.target.value };
                          setFormData({ ...formData, delegaciones_visibilidad: arr });
                        }}
                        data-testid={`delegacion-supervisor-${idx}`}
                      >
                        <option value="">— Selecciona el supervisor a cubrir —</option>
                        {opciones.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-slate-500">{d.activo !== false ? "Activa" : "Inactiva"}</span>
                        <Switch
                          checked={d.activo !== false}
                          onCheckedChange={(checked) => {
                            const arr = [...formData.delegaciones_visibilidad];
                            arr[idx] = { ...arr[idx], activo: checked };
                            setFormData({ ...formData, delegaciones_visibilidad: arr });
                          }}
                          data-testid={`delegacion-activo-${idx}`}
                        />
                      </div>
                      <Button
                        type="button" variant="ghost" size="sm" className="text-red-600 h-8 px-2"
                        onClick={() => setFormData({ ...formData, delegaciones_visibilidad: formData.delegaciones_visibilidad.filter((_, i) => i !== idx) })}
                        data-testid={`delegacion-quitar-${idx}`}
                      >✕</Button>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {[
                        { id: "control_renovaciones", label: "Control de Renovaciones" },
                        { id: "control_cotizaciones", label: "Control de Cotizaciones" },
                        { id: "pipelines", label: "Pipelines" },
                        { id: "estadisticas_renovaciones", label: "Estadísticas de Renovaciones" },
                      ].map(m => (
                        <label key={m.id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <input
                            type="checkbox"
                            className="accent-amber-600"
                            data-testid={`delegacion-modulo-${idx}-${m.id}`}
                            checked={(d.modulos || []).includes(m.id)}
                            onChange={(e) => {
                              const arr = [...formData.delegaciones_visibilidad];
                              const mods = new Set(arr[idx].modulos || []);
                              e.target.checked ? mods.add(m.id) : mods.delete(m.id);
                              arr[idx] = { ...arr[idx], modulos: Array.from(mods) };
                              setFormData({ ...formData, delegaciones_visibilidad: arr });
                            }}
                          />
                          {m.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  );
                })}
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setFormData({ ...formData, delegaciones_visibilidad: [...(formData.delegaciones_visibilidad || []), { supervisor_id: "", modulos: [], activo: true }] })}
                  data-testid="delegacion-agregar-btn"
                >+ Agregar cobertura</Button>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="home_page" className="flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Ventana de Inicio
                </Label>
                <Select 
                  value={formData.home_page === "custom" ? "custom" : (formData.home_page || "none")} 
                  onValueChange={(value) => {
                    if (value === "custom") {
                      setFormData({...formData, home_page: "custom"});
                    } else {
                      setFormData({...formData, home_page: value === "none" ? "" : value, home_page_custom: ""});
                    }
                  }}
                >
                  <SelectTrigger id="home_page" data-testid="home-page-select">
                    <SelectValue placeholder="Seleccionar página de inicio..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Por defecto (Dashboard)</SelectItem>
                    {menus.map(menu => (
                      <SelectItem key={menu.id} value={menu.id}>
                        {menu.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="custom" className="text-emerald-600 font-medium">
                      URL Personalizada
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Custom URL input */}
                {formData.home_page === "custom" && (
                  <div className="mt-2">
                    <Input
                      type="text"
                      placeholder="Ej: /app/entregas o /mensajeria/solicitudes"
                      value={formData.home_page_custom || ""}
                      onChange={(e) => setFormData({...formData, home_page_custom: e.target.value})}
                      className="font-mono text-sm"
                      data-testid="home-page-custom-input"
                    />
                    <p className="text-xs text-emerald-600 mt-1">
                      Ingresa la ruta completa donde deseas que inicie el usuario
                    </p>
                  </div>
                )}
                
                <p className="text-xs text-slate-500">Página que se abre automáticamente al iniciar sesión</p>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <Label htmlFor="is_active" className="cursor-pointer font-medium">Usuario activo</Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Los usuarios inactivos no pueden iniciar sesión</p>
                </div>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  data-testid="user-active-toggle"
                />
              </div>

              {/* Mantener información visible para supervisores aunque el usuario esté inactivo */}
              {!formData.is_active && (
                <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div>
                    <Label htmlFor="mantener_visible_inactivo" className="cursor-pointer font-medium">Mantener información visible para supervisores</Label>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Aunque el usuario esté inactivo, sus datos (Control de Renovaciones, Checklist, etc.) seguirán visibles para sus supervisores.</p>
                  </div>
                  <Switch
                    id="mantener_visible_inactivo"
                    checked={formData.mantener_visible_inactivo || false}
                    onCheckedChange={(checked) => setFormData({...formData, mantener_visible_inactivo: checked})}
                    data-testid="user-mantener-visible-toggle"
                  />
                </div>
              )}

              {/* Permiso para aprobar requisiciones */}
              <div className="flex items-center space-x-3 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                <input
                  type="checkbox"
                  id="es_aprobador_requisiciones"
                  checked={formData.es_aprobador_requisiciones || false}
                  onChange={(e) => setFormData({...formData, es_aprobador_requisiciones: e.target.checked})}
                  className="w-4 h-4 text-violet-600 rounded border-violet-300 focus:ring-violet-500"
                  data-testid="es-aprobador-requisiciones-checkbox"
                />
                <div>
                  <Label htmlFor="es_aprobador_requisiciones" className="text-sm font-medium text-violet-800 dark:text-violet-300 cursor-pointer">
                    Aprobador de requisiciones
                  </Label>
                  <p className="text-xs text-violet-600 dark:text-violet-400">
                    Este usuario puede ver y aprobar todas las requisiciones del sistema
                  </p>
                </div>
              </div>

              {/* Permiso para eliminar requisiciones */}
              <div className="flex items-center space-x-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <input
                  type="checkbox"
                  id="can_delete_requisiciones"
                  checked={formData.can_delete_requisiciones || false}
                  onChange={(e) => setFormData({...formData, can_delete_requisiciones: e.target.checked})}
                  className="w-4 h-4 text-red-600 rounded border-red-300 focus:ring-red-500"
                  data-testid="can-delete-requisiciones-checkbox"
                />
                <div>
                  <Label htmlFor="can_delete_requisiciones" className="text-sm font-medium text-red-800 dark:text-red-300 cursor-pointer">
                    Puede eliminar requisiciones
                  </Label>
                  <p className="text-xs text-red-600 dark:text-red-400">
                    Este usuario puede autorizar la eliminación de requisiciones
                  </p>
                </div>
              </div>

              {/* Permiso para eliminar transacciones financieras */}
              <div className="flex items-center space-x-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <input
                  type="checkbox"
                  id="can_delete_transacciones_financieras"
                  checked={formData.can_delete_transacciones_financieras || false}
                  onChange={(e) => setFormData({...formData, can_delete_transacciones_financieras: e.target.checked})}
                  className="w-4 h-4 text-orange-600 rounded border-orange-300 focus:ring-orange-500"
                  data-testid="can-delete-transacciones-checkbox"
                />
                <div>
                  <Label htmlFor="can_delete_transacciones_financieras" className="text-sm font-medium text-orange-800 dark:text-orange-300 cursor-pointer">
                    Puede eliminar transacciones financieras
                  </Label>
                  <p className="text-xs text-orange-600 dark:text-orange-400">
                    Permite eliminar transacciones en Caja Chica y Cuentas por Pagar
                  </p>
                </div>
              </div>

              {/* Permiso para autorizar eliminaciones financieras */}
              <div className="flex items-center space-x-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <input
                  type="checkbox"
                  id="can_authorize_financial_deletions"
                  checked={formData.can_authorize_financial_deletions || false}
                  onChange={(e) => setFormData({...formData, can_authorize_financial_deletions: e.target.checked})}
                  className="w-4 h-4 text-purple-600 rounded border-purple-300 focus:ring-purple-500"
                  data-testid="can-authorize-deletions-checkbox"
                />
                <div>
                  <Label htmlFor="can_authorize_financial_deletions" className="text-sm font-medium text-purple-800 dark:text-purple-300 cursor-pointer">
                    Puede autorizar eliminaciones financieras
                  </Label>
                  <p className="text-xs text-purple-600 dark:text-purple-400">
                    Su clave puede usarse para autorizar la eliminación de transacciones en Caja Chica y Cuentas por Pagar
                  </p>
                </div>
              </div>

              {/* Permiso para editar transacciones de caja chica */}
              <div className="flex items-center space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <input
                  type="checkbox"
                  id="puede_editar_caja_chica"
                  checked={formData.puede_editar_caja_chica || false}
                  onChange={(e) => setFormData({...formData, puede_editar_caja_chica: e.target.checked})}
                  className="w-4 h-4 text-blue-600 rounded border-blue-300 focus:ring-blue-500"
                  data-testid="puede-editar-caja-chica-checkbox"
                />
                <div>
                  <Label htmlFor="puede_editar_caja_chica" className="text-sm font-medium text-blue-800 dark:text-blue-300 cursor-pointer">
                    Puede editar transacciones de Caja Chica
                  </Label>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Permite editar gastos ya registrados en el módulo de Caja Chica
                  </p>
                </div>
              </div>

              {/* Permiso para eliminar movimientos de Gestión Humana */}
              <div className="flex items-center space-x-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <input
                  type="checkbox"
                  id="can_delete_movimientos_gh"
                  checked={formData.can_delete_movimientos_gh || false}
                  onChange={(e) => setFormData({...formData, can_delete_movimientos_gh: e.target.checked})}
                  className="w-4 h-4 text-purple-600 rounded border-purple-300 focus:ring-purple-500"
                  data-testid="can-delete-movimientos-gh-checkbox"
                />
                <div>
                  <Label htmlFor="can_delete_movimientos_gh" className="text-sm font-medium text-purple-800 dark:text-purple-300 cursor-pointer">
                    Puede eliminar movimientos de Gestión Humana
                  </Label>
                  <p className="text-xs text-purple-600 dark:text-purple-400">
                    Permite eliminar permisos, tardanzas, ausencias y cumpleaños (requiere contraseña)
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
                <input
                  type="checkbox"
                  id="can_view_all_mensajeria"
                  checked={formData.can_view_all_mensajeria || false}
                  onChange={(e) => setFormData({...formData, can_view_all_mensajeria: e.target.checked})}
                  className="w-4 h-4 text-cyan-600 rounded border-cyan-300 focus:ring-cyan-500"
                  data-testid="can-view-all-mensajeria-checkbox"
                />
                <div>
                  <Label htmlFor="can_view_all_mensajeria" className="text-sm font-medium text-cyan-800 dark:text-cyan-300 cursor-pointer">
                    Puede ver toda la mensajería
                  </Label>
                  <p className="text-xs text-cyan-600 dark:text-cyan-400">
                    Permite visualizar todas las solicitudes de mensajería (pendientes, en proceso, completadas)
                  </p>
                </div>
              </div>

              {/* Permiso Solo lectura encuestas */}
              <div className="flex items-center space-x-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <input
                  type="checkbox"
                  id="solo_lectura_encuestas"
                  checked={formData.solo_lectura_encuestas || false}
                  onChange={(e) => setFormData({...formData, solo_lectura_encuestas: e.target.checked})}
                  className="w-4 h-4 text-indigo-600 rounded border-indigo-300 focus:ring-indigo-500"
                  data-testid="solo-lectura-encuestas-checkbox"
                />
                <div>
                  <Label htmlFor="solo_lectura_encuestas" className="text-sm font-medium text-indigo-800 dark:text-indigo-300 cursor-pointer">
                    Solo lectura encuestas
                  </Label>
                  <p className="text-xs text-indigo-600 dark:text-indigo-400">
                    Puede ver el módulo de Encuestas pero solo puede copiar los links de los destinatarios; el resto de acciones queda oculto
                  </p>
                </div>
              </div>

              {/* Permiso Solo lectura resultados propios */}
              <div className="flex items-center space-x-3 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                <input
                  type="checkbox"
                  id="solo_resultados_propios"
                  checked={formData.solo_resultados_propios || false}
                  onChange={(e) => setFormData({...formData, solo_resultados_propios: e.target.checked})}
                  className="w-4 h-4 text-violet-600 rounded border-violet-300 focus:ring-violet-500"
                  data-testid="solo-resultados-propios-checkbox"
                />
                <div>
                  <Label htmlFor="solo_resultados_propios" className="text-sm font-medium text-violet-800 dark:text-violet-300 cursor-pointer">
                    Solo lectura resultados propios
                  </Label>
                  <p className="text-xs text-violet-600 dark:text-violet-400">
                    En Resultados de Encuestas solo verá las estadísticas de las encuestas donde él es el ejecutivo encuestado; las de otros usuarios quedan ocultas
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800">
                <input
                  type="checkbox"
                  id="puede_publicar"
                  checked={formData.puede_publicar || false}
                  onChange={(e) => setFormData({...formData, puede_publicar: e.target.checked})}
                  className="w-4 h-4 text-teal-600 rounded border-teal-300 focus:ring-teal-500"
                  data-testid="puede-publicar-checkbox"
                />
                <div>
                  <Label htmlFor="puede_publicar" className="text-sm font-medium text-teal-800 dark:text-teal-300 cursor-pointer">
                    Puede Publicar en Informaciones Generales
                  </Label>
                  <p className="text-xs text-teal-600 dark:text-teal-400">
                    Permite crear, editar y eliminar contenido en el módulo de Informaciones Generales
                  </p>
                </div>
              </div>

              {/* Cotizaciones Departments */}
              <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <Label className="text-sm font-medium text-indigo-800 dark:text-indigo-300">
                  Departamentos de Cotizaciones
                </Label>
                <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-3">
                  Seleccione los departamentos que el usuario puede ver. Si no selecciona ninguno, tendrá acceso a todos.
                </p>
                <div className="space-y-2">
                  {[
                    { value: "salud_local", label: "Salud Local" },
                    { value: "salud_internacional", label: "Salud Internacional" },
                    { value: "riesgos_generales", label: "Riesgos Generales" }
                  ].map(dept => (
                    <div key={dept.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`cot_dept_${dept.value}`}
                        checked={(formData.cotizaciones_departamentos || []).includes(dept.value)}
                        onChange={(e) => {
                          const current = formData.cotizaciones_departamentos || [];
                          const updated = e.target.checked
                            ? [...current, dept.value]
                            : current.filter(d => d !== dept.value);
                          setFormData({ ...formData, cotizaciones_departamentos: updated });
                        }}
                        className="w-4 h-4 text-indigo-600 rounded border-indigo-300 focus:ring-indigo-500"
                        data-testid={`cot-dept-${dept.value}`}
                      />
                      <Label htmlFor={`cot_dept_${dept.value}`} className="text-sm text-indigo-700 dark:text-indigo-300 cursor-pointer">
                        {dept.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </div>
            
            <DialogFooter className="flex-shrink-0 border-t pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                data-testid="save-user-button"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Dialog */}
        <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
          <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope'] flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#0C314C]" />
                Importar Usuarios desde Excel
              </DialogTitle>
              <DialogDescription>
                {bulkStep === 1 && "Sube un archivo Excel (.xlsx) con los datos de los usuarios"}
                {bulkStep === 2 && "Verifica el mapeo de columnas generado por la IA"}
                {bulkStep === 3 && "Resultado de la importación"}
              </DialogDescription>
            </DialogHeader>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 py-4 border-b dark:border-slate-700">
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 1 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">1</span>
                Subir
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 2 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
                Mapear
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 3 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">3</span>
                Resultado
              </div>
            </div>

            <div className="flex-1 overflow-auto py-4">
              {/* Step 1: Upload */}
              {bulkStep === 1 && (
                <div className="flex flex-col items-center justify-center py-12">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  
                  <div className="w-20 h-20 rounded-full bg-[#0C314C]/10 flex items-center justify-center mb-6">
                    <FileSpreadsheet className="w-10 h-10 text-[#0C314C]" />
                  </div>
                  
                  {bulkLoading ? (
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-[#0C314C] mx-auto mb-4" />
                      <p className="text-slate-600 dark:text-slate-400">Analizando archivo con IA...</p>
                    </div>
                  ) : (
                    <>
                      <Button 
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white mb-4"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Seleccionar Archivo Excel
                      </Button>
                      <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md">
                        La IA analizará las columnas de tu archivo y las mapeará automáticamente a los campos de usuario correspondientes.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Step 2: Mapping */}
              {bulkStep === 2 && bulkPreview && (
                <div className="space-y-6">
                  {/* Mapping Section */}
                  <div>
                    <h3 className="font-semibold mb-3 text-slate-900 dark:text-white flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Mapeo de Columnas (Generado por IA)
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {bulkPreview.headers.map(header => (
                        <div key={header} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate" title={header}>
                            {header}
                          </span>
                          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <Select 
                            value={bulkMapping[header] || "_none"} 
                            onValueChange={(value) => updateMapping(header, value === "_none" ? "" : value)}
                          >
                            <SelectTrigger className="w-36 text-xs h-8">
                              <SelectValue placeholder="No mapear" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">No mapear</SelectItem>
                              {userFields.map(field => (
                                <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Defaults */}
                  <div className="flex gap-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-xs text-amber-700 dark:text-amber-300">Contraseña por defecto</Label>
                      <Input 
                        value={bulkPreview.default_password} 
                        onChange={(e) => setBulkPreview({...bulkPreview, default_password: e.target.value})}
                        className="h-8 text-sm mt-1"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-amber-700 dark:text-amber-300">Rol por defecto</Label>
                      <Select 
                        value={bulkPreview.default_role} 
                        onValueChange={(value) => setBulkPreview({...bulkPreview, default_role: value})}
                      >
                        <SelectTrigger className="h-8 text-sm mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {dynamicRoles.map(role => (
                            <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Preview Table */}
                  <div>
                    <h3 className="font-semibold mb-3 text-slate-900 dark:text-white">
                      Vista previa ({bulkPreview.total_rows} registros)
                    </h3>
                    <ScrollArea className="h-48 border rounded-lg dark:border-slate-700">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                          <tr>
                            {bulkPreview.headers.slice(0, 5).map(h => (
                              <th key={h} className="text-left p-2 font-medium text-slate-600 dark:text-slate-300">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                          {bulkPreview.sample_data.map((row, idx) => (
                            <tr key={idx}>
                              {bulkPreview.headers.slice(0, 5).map(h => (
                                <td key={h} className="p-2 text-slate-700 dark:text-slate-300">{row[h] || '-'}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </div>
                </div>
              )}

              {/* Step 3: Results */}
              {bulkStep === 3 && bulkResult && (
                <div className="flex flex-col items-center py-8">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                    bulkResult.created > 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
                  }`}>
                    {bulkResult.created > 0 ? (
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    ) : (
                      <AlertCircle className="w-10 h-10 text-amber-600" />
                    )}
                  </div>

                  <h3 className="text-xl font-semibold mb-2 text-slate-900 dark:text-white">
                    {bulkResult.created > 0 ? "Importación Completada" : "Importación con Advertencias"}
                  </h3>
                  
                  <div className="flex gap-8 mb-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-600">{bulkResult.created}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Creados</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-amber-600">{bulkResult.skipped}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Omitidos</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-slate-600 dark:text-slate-300">{bulkResult.total_processed}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Total</p>
                    </div>
                  </div>

                  {bulkResult.errors.length > 0 && (
                    <div className="w-full max-w-lg">
                      <h4 className="font-medium mb-2 text-slate-700 dark:text-slate-300">Detalles de errores:</h4>
                      <ScrollArea className="h-32 border rounded-lg p-3 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
                        {bulkResult.errors.map((err, idx) => (
                          <p key={idx} className="text-sm text-red-700 dark:text-red-300 mb-1">{err}</p>
                        ))}
                      </ScrollArea>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="border-t dark:border-slate-700 pt-4">
              {bulkStep === 1 && (
                <Button variant="outline" onClick={() => setShowBulkDialog(false)}>
                  Cancelar
                </Button>
              )}
              {bulkStep === 2 && (
                <>
                  <Button variant="outline" onClick={() => setBulkStep(1)}>
                    Volver
                  </Button>
                  <Button 
                    onClick={executeBulkImport}
                    disabled={bulkLoading || !Object.values(bulkMapping).includes('email')}
                    className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                  >
                    {bulkLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    {bulkLoading ? "Importando..." : `Importar ${bulkPreview?.total_rows || 0} Usuarios`}
                  </Button>
                </>
              )}
              {bulkStep === 3 && (
                <Button onClick={() => setShowBulkDialog(false)} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
                  Cerrar
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Consolidate duplicates dialog */}
        <Dialog open={showConsolidateDialog} onOpenChange={setShowConsolidateDialog}>
          <DialogContent className="sm:max-w-[640px]" data-testid="consolidate-users-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Merge className="w-5 h-5 text-amber-600" />
                Consolidar usuarios duplicados
              </DialogTitle>
              <DialogDescription>
                Si por error se crearon usuarios con typos (ej: "Yamilex Abreuyy", "Yamilex YY"), unifica
                todas sus estadísticas y registros en un único usuario canónico. Los registros (checklists,
                mensajería, tickets, cotizaciones, etc.) se actualizan en TODA la plataforma y los usuarios
                duplicados son eliminados.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Auto-detection panel */}
              <Card className="border-blue-200 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-950/20">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-blue-900 dark:text-blue-200 flex items-center gap-1.5">
                      <Search className="w-4 h-4" />
                      Detección automática de typos
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchDuplicateSuggestions}
                      disabled={scanningDuplicates}
                      data-testid="rescan-duplicates-btn"
                      className="h-7 text-xs"
                    >
                      {scanningDuplicates ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RotateCw className="w-3.5 h-3.5 mr-1.5" />}
                      Reescanear
                    </Button>
                  </div>
                  {scanningDuplicates ? (
                    <p className="text-xs text-slate-500">Escaneando usuarios…</p>
                  ) : !autoSuggestions ? (
                    <p className="text-xs text-slate-500">Ejecuta el escaneo para detectar candidatos.</p>
                  ) : autoSuggestions.suggestions.length === 0 ? (
                    <p className="text-xs text-emerald-700 dark:text-emerald-400" data-testid="no-duplicates-msg">
                      ✓ No se detectaron duplicados (similitud ≥ {Math.round(autoSuggestions.threshold * 100)}%) entre los {autoSuggestions.scanned_users} usuarios.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-44 overflow-y-auto" data-testid="duplicate-suggestions-list">
                      {autoSuggestions.suggestions.map((sug, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between gap-2 p-2 rounded border border-blue-200 dark:border-blue-900 bg-white dark:bg-slate-900"
                          data-testid={`duplicate-suggestion-${idx}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                              {sug.canonical}
                              <span className="ml-2 text-xs font-normal text-slate-500">
                                ({sug.total_users} usuarios)
                              </span>
                            </div>
                            <div className="text-xs text-slate-600 dark:text-slate-400 truncate">
                              Aliases: {sug.aliases.map((a) => `${a.name} (${a.similarity}%)`).join(", ")}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => applySuggestion(sug)}
                            data-testid={`apply-suggestion-${idx}`}
                            className="text-xs h-7 shrink-0"
                          >
                            Usar
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div>
                <Label htmlFor="canonical-name">Nombre canónico (el correcto)</Label>
                <Input
                  id="canonical-name"
                  value={canonicalName}
                  onChange={(e) => setCanonicalName(e.target.value)}
                  placeholder="Ej: Yamilex Abreu"
                  data-testid="consolidate-canonical-input"
                />
              </div>
              <div>
                <Label htmlFor="aliases">Alias a unificar (uno por línea o separados por coma)</Label>
                <textarea
                  id="aliases"
                  className="w-full min-h-[110px] rounded-md border border-slate-300 dark:border-slate-700 bg-transparent p-2 text-sm font-mono"
                  value={aliasesInput}
                  onChange={(e) => setAliasesInput(e.target.value)}
                  placeholder={"Yamilex Abreuyy\nYamilex YY\nYamilex Abreuya\nYamilex Abreuy"}
                  data-testid="consolidate-aliases-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Estos nombres serán renombrados a "{canonicalName || "<canónico>"}" en todas las collections.
                  Los registros de usuarios con esos nombres se eliminarán (manteniendo solo el canónico).
                </p>
              </div>
              {consolidateResult && (
                <Card className="bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span className="font-semibold text-sm">
                        {consolidateResult.dry_run ? "Vista previa (sin escribir)" : "Resultado aplicado"}
                      </span>
                    </div>
                    <div className="text-xs space-y-1">
                      {Object.keys(consolidateResult.collections_touched || {}).length === 0 ? (
                        <p className="text-slate-500">No se encontraron registros con esos alias.</p>
                      ) : (
                        Object.entries(consolidateResult.collections_touched).map(([col, fields]) => (
                          <div key={col}>
                            <span className="font-mono text-slate-700 dark:text-slate-300">{col}:</span>{" "}
                            <span className="text-slate-600 dark:text-slate-400">
                              {Object.entries(fields)
                                .map(([f, n]) => `${f} (${n})`)
                                .join(", ")}
                            </span>
                          </div>
                        ))
                      )}
                      {consolidateResult.users_deleted?.length > 0 && (
                        <p className="mt-2 text-amber-700 dark:text-amber-400">
                          🗑 Eliminados: {consolidateResult.users_deleted.map((u) => u.name).join(", ")}
                        </p>
                      )}
                      {consolidateResult.users_kept && (
                        <p className="text-emerald-700 dark:text-emerald-400">
                          ✓ Mantenido: {consolidateResult.users_kept.name} ({consolidateResult.users_kept.email})
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowConsolidateDialog(false)}
                data-testid="consolidate-cancel-btn"
              >
                Cerrar
              </Button>
              <Button
                variant="outline"
                onClick={() => handleConsolidate(true)}
                disabled={consolidating}
                data-testid="consolidate-dry-run-btn"
              >
                {consolidating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Vista previa
              </Button>
              <Button
                onClick={() => handleConsolidate(false)}
                disabled={consolidating}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="consolidate-apply-btn"
              >
                {consolidating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Merge className="w-4 h-4 mr-2" />}
                Consolidar y eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Historial de Coberturas de Supervisor */}
        <Dialog open={showHistorialCoberturas} onOpenChange={setShowHistorialCoberturas}>
          <DialogContent className="sm:max-w-[700px] max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Historial de Coberturas de Supervisor</DialogTitle>
              <DialogDescription>Registro de qué usuario cubrió a qué supervisor y en qué fechas</DialogDescription>
            </DialogHeader>
            {loadingHistCoberturas ? (
              <div className="text-center py-8 text-slate-500">Cargando historial...</div>
            ) : historialCoberturas.length > 0 ? (
              <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1" data-testid="historial-coberturas-lista">
                {historialCoberturas.map((h) => (
                  <div key={h.id} className="border rounded-lg p-3 text-sm" data-testid={`cobertura-hist-${h.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-medium">{h.usuario_nombre} <span className="text-slate-400 font-normal">cubre a</span> {h.supervisor_nombre}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${h.fecha_fin ? "bg-slate-100 text-slate-600" : "bg-green-100 text-green-700"}`}>
                        {h.fecha_fin ? "Finalizada" : "Activa"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                      <p>Módulos: {(h.modulos || []).join(", ") || "—"}</p>
                      <p>Desde: {h.fecha_inicio ? new Date(h.fecha_inicio).toLocaleString("es-DO") : "—"}
                        {h.fecha_fin ? ` — Hasta: ${new Date(h.fecha_fin).toLocaleString("es-DO")}` : ""}</p>
                      {h.registrado_por && <p>Activada por: {h.registrado_por}{h.cerrado_por ? ` — Desactivada por: ${h.cerrado_por}` : ""}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>No hay coberturas registradas</p>
                <p className="text-xs mt-1">El historial se genera al activar o desactivar una cobertura</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHistorialCoberturas(false)} data-testid="historial-coberturas-cerrar">Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
